import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdCheck } from 'react-icons/md';
import '../master/MasterData.css';

interface AdjustmentItem {
  itemCode: string;
  itemName: string;
  systemQty: number;
  actualQty: number;
  difference: number;
  unit: string;
  unitCost: number;
  adjustmentValue: number;
}

interface StockAdjustment {
  adjustmentId: number;
  adjustmentNo: string;
  adjustmentDate: string;
  adjustmentType: 'PHYSICAL_COUNT' | 'DAMAGE' | 'OBSOLETE' | 'CORRECTION' | 'WRITE_OFF';
  location: string;
  totalAdjustmentValue: number;
  performedBy: string;
  approvedBy: string;
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'POSTED';
  items: AdjustmentItem[];
  reason: string;
  remarks: string;
}

const StockAdjustment: React.FC = () => {
  const [adjustments, setAdjustments] = useState<StockAdjustment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState<StockAdjustment | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    adjustmentType: 'PHYSICAL_COUNT' as 'PHYSICAL_COUNT' | 'DAMAGE' | 'OBSOLETE' | 'CORRECTION' | 'WRITE_OFF',
    location: '',
    reason: '',
    remarks: ''
  });
  const [items, setItems] = useState<AdjustmentItem[]>([]);
  const [newItem, setNewItem] = useState({
    itemCode: '',
    itemName: '',
    systemQty: 0,
    actualQty: 0,
    unit: 'pcs',
    unitCost: 0
  });

  useEffect(() => {
    // TODO: Fetch stock adjustments from API
    setAdjustments([
      {
        adjustmentId: 1,
        adjustmentNo: 'ADJ-2025-0001',
        adjustmentDate: '2025-02-28',
        adjustmentType: 'PHYSICAL_COUNT',
        location: 'Warehouse A - Raw Materials',
        totalAdjustmentValue: -75000,
        performedBy: 'John Warehouse',
        approvedBy: 'Manager Approved',
        status: 'POSTED',
        items: [
          {
            itemCode: 'RM-001',
            itemName: 'Polyurethane Foam - High Density',
            systemQty: 5000,
            actualQty: 4500,
            difference: -500,
            unit: 'kg',
            unitCost: 150,
            adjustmentValue: -75000
          }
        ],
        reason: 'Annual physical inventory count - shortage found',
        remarks: 'Discrepancy investigated and confirmed'
      },
      {
        adjustmentId: 2,
        adjustmentNo: 'ADJ-2025-0002',
        adjustmentDate: '2025-03-01',
        adjustmentType: 'DAMAGE',
        location: 'Warehouse B - Finished Goods',
        totalAdjustmentValue: -90000,
        performedBy: 'QC Department',
        approvedBy: '',
        status: 'PENDING_APPROVAL',
        items: [
          {
            itemCode: 'FG-001',
            itemName: 'Executive Office Chair - Black',
            systemQty: 150,
            actualQty: 120,
            difference: -30,
            unit: 'pcs',
            unitCost: 3000,
            adjustmentValue: -90000
          }
        ],
        reason: 'Water damage from roof leak',
        remarks: 'Awaiting manager approval for write-off'
      },
      {
        adjustmentId: 3,
        adjustmentNo: 'ADJ-2025-0003',
        adjustmentDate: '2025-03-03',
        adjustmentType: 'CORRECTION',
        location: 'Warehouse A - Raw Materials',
        totalAdjustmentValue: 30000,
        performedBy: 'System Admin',
        approvedBy: '',
        status: 'DRAFT',
        items: [
          {
            itemCode: 'RM-002',
            itemName: 'Fabric - Premium Grade',
            systemQty: 2900,
            actualQty: 3050,
            difference: 150,
            unit: 'meters',
            unitCost: 200,
            adjustmentValue: 30000
          }
        ],
        reason: 'Data entry error correction',
        remarks: 'Previous transaction was incorrectly recorded'
      }
    ]);
  }, []);

  const filteredAdjustments = adjustments.filter(adjustment =>
    adjustment.adjustmentNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    adjustment.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingAdjustment(null);
    setViewMode(false);
    setFormData({
      adjustmentType: 'PHYSICAL_COUNT',
      location: '',
      reason: '',
      remarks: ''
    });
    setItems([]);
    setNewItem({
      itemCode: '',
      itemName: '',
      systemQty: 0,
      actualQty: 0,
      unit: 'pcs',
      unitCost: 0
    });
    setShowModal(true);
  };

  const handleEdit = (adjustment: StockAdjustment) => {
    setEditingAdjustment(adjustment);
    setViewMode(false);
    setFormData({
      adjustmentType: adjustment.adjustmentType,
      location: adjustment.location,
      reason: adjustment.reason,
      remarks: adjustment.remarks
    });
    setItems([...adjustment.items]);
    setShowModal(true);
  };

  const handleView = (adjustment: StockAdjustment) => {
    setEditingAdjustment(adjustment);
    setViewMode(true);
    setFormData({
      adjustmentType: adjustment.adjustmentType,
      location: adjustment.location,
      reason: adjustment.reason,
      remarks: adjustment.remarks
    });
    setItems([...adjustment.items]);
    setShowModal(true);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddItem = () => {
    if (!newItem.itemCode || !newItem.itemName || newItem.unitCost <= 0) {
      alert('Please fill in all required item fields');
      return;
    }

    const difference = newItem.actualQty - newItem.systemQty;
    const adjustmentValue = difference * newItem.unitCost;

    const item: AdjustmentItem = {
      ...newItem,
      difference,
      adjustmentValue
    };

    setItems([...items, item]);
    setNewItem({
      itemCode: '',
      itemName: '',
      systemQty: 0,
      actualQty: 0,
      unit: 'pcs',
      unitCost: 0
    });
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!formData.location || !formData.reason || items.length === 0) {
      alert('Please fill in all required fields and add at least one item');
      return;
    }

    const totalAdjustmentValue = items.reduce((sum, item) => sum + item.adjustmentValue, 0);
    const adjustmentNo = `ADJ-2025-${(adjustments.length + 1).toString().padStart(4, '0')}`;

    const newAdjustment: StockAdjustment = {
      adjustmentId: adjustments.length + 1,
      adjustmentNo,
      adjustmentDate: new Date().toISOString().split('T')[0],
      adjustmentType: formData.adjustmentType,
      location: formData.location,
      totalAdjustmentValue,
      performedBy: 'Current User', // TODO: Get from auth context
      approvedBy: '',
      status: 'DRAFT',
      items: [...items],
      reason: formData.reason,
      remarks: formData.remarks
    };

    if (editingAdjustment) {
      setAdjustments(adjustments.map(a =>
        a.adjustmentId === editingAdjustment.adjustmentId ? { ...newAdjustment, adjustmentId: editingAdjustment.adjustmentId, adjustmentNo: editingAdjustment.adjustmentNo } : a
      ));
      alert('Adjustment updated successfully!');
    } else {
      setAdjustments([newAdjustment, ...adjustments]);
      alert('Adjustment created successfully!');
    }

    setShowModal(false);
  };

  const handleDelete = (adjustmentId: number) => {
    if (window.confirm('Are you sure you want to delete this adjustment?')) {
      setAdjustments(adjustments.filter(a => a.adjustmentId !== adjustmentId));
    }
  };

  const handleApprove = (adjustmentId: number) => {
    if (window.confirm('Approve this adjustment?')) {
      alert('Adjustment approved');
      // TODO: Update status to APPROVED
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'POSTED': return 'badge-success';
      case 'APPROVED': return 'badge-info';
      case 'PENDING_APPROVAL': return 'badge-warning';
      case 'REJECTED': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const getAdjustmentTypeBadge = (type: string) => {
    switch (type) {
      case 'PHYSICAL_COUNT': return <span className="badge badge-primary">PHYSICAL COUNT</span>;
      case 'DAMAGE': return <span className="badge badge-danger">DAMAGE</span>;
      case 'OBSOLETE': return <span className="badge badge-warning">OBSOLETE</span>;
      case 'CORRECTION': return <span className="badge badge-info">CORRECTION</span>;
      case 'WRITE_OFF': return <span className="badge badge-red">WRITE OFF</span>;
      default: return <span className="badge badge-secondary">{type}</span>;
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Stock Adjustment</h2>
            <p className="subtitle">Manage inventory adjustments and physical counts</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Create New Adjustment
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search adjustments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Adjustment No</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Adjustment Value</th>
                  <th>Performed By</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAdjustments.map(adjustment => (
                  <tr key={adjustment.adjustmentId}>
                    <td className="code">{adjustment.adjustmentNo}</td>
                    <td>{new Date(adjustment.adjustmentDate).toLocaleDateString('en-US')}</td>
                    <td>{getAdjustmentTypeBadge(adjustment.adjustmentType)}</td>
                    <td>{adjustment.location}</td>
                    <td className="amount" style={{
                      color: adjustment.totalAdjustmentValue >= 0 ? '#28a745' : '#dc3545',
                      fontWeight: 'bold'
                    }}>
                      ₱{adjustment.totalAdjustmentValue.toLocaleString()}
                    </td>
                    <td>{adjustment.performedBy}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(adjustment.status)}`}>
                        {adjustment.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(adjustment)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        {adjustment.status === 'PENDING_APPROVAL' && (
                          <button
                            className="btn-icon"
                            onClick={() => handleApprove(adjustment.adjustmentId)}
                            title="Approve"
                            style={{ color: '#28a745' }}
                          >
                            <MdCheck />
                          </button>
                        )}
                        {adjustment.status === 'DRAFT' && (
                          <>
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => handleEdit(adjustment)}
                              title="Edit"
                            >
                              <MdEdit />
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(adjustment.adjustmentId)}
                              title="Delete"
                            >
                              <MdDelete />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredAdjustments.length === 0 && (
              <div className="no-data">
                <p>No adjustments found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px' }}>
            <div className="modal-header">
              <h3>{viewMode ? 'View Adjustment' : editingAdjustment ? 'Edit Adjustment' : 'Create New Adjustment'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Header Information */}
              <div className="form-row">
                <div className="form-group">
                  <label>Adjustment Type <span style={{ color: 'red' }}>*</span></label>
                  <select
                    value={formData.adjustmentType}
                    onChange={(e) => handleFormChange('adjustmentType', e.target.value)}
                    disabled={viewMode}
                  >
                    <option value="PHYSICAL_COUNT">Physical Count</option>
                    <option value="DAMAGE">Damage</option>
                    <option value="OBSOLETE">Obsolete</option>
                    <option value="CORRECTION">Correction</option>
                    <option value="WRITE_OFF">Write Off</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Location <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleFormChange('location', e.target.value)}
                    placeholder="Enter location"
                    disabled={viewMode}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Reason <span style={{ color: 'red' }}>*</span></label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => handleFormChange('reason', e.target.value)}
                  placeholder="Enter reason for adjustment"
                  rows={2}
                  disabled={viewMode}
                />
              </div>

              <div className="form-group">
                <label>Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => handleFormChange('remarks', e.target.value)}
                  placeholder="Enter additional remarks"
                  rows={2}
                  disabled={viewMode}
                />
              </div>

              <hr style={{ margin: '20px 0' }} />

              {/* Items Section */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '15px' }}>Adjustment Items</h4>

                {!viewMode && (
                  <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px', marginBottom: '15px' }}>
                    <h5 style={{ marginBottom: '10px', fontSize: '14px' }}>Add Item</h5>
                    <div className="form-row">
                      <div className="form-group" style={{ flex: '1' }}>
                        <label style={{ fontSize: '12px' }}>Item Code <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="text"
                          value={newItem.itemCode}
                          onChange={(e) => setNewItem({ ...newItem, itemCode: e.target.value })}
                          placeholder="Item code"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="form-group" style={{ flex: '2' }}>
                        <label style={{ fontSize: '12px' }}>Item Name <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="text"
                          value={newItem.itemName}
                          onChange={(e) => setNewItem({ ...newItem, itemName: e.target.value })}
                          placeholder="Item name"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label style={{ fontSize: '12px' }}>System Qty <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="number"
                          value={newItem.systemQty}
                          onChange={(e) => setNewItem({ ...newItem, systemQty: Number(e.target.value) })}
                          placeholder="0"
                          min="0"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '12px' }}>Actual Qty <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="number"
                          value={newItem.actualQty}
                          onChange={(e) => setNewItem({ ...newItem, actualQty: Number(e.target.value) })}
                          placeholder="0"
                          min="0"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '12px' }}>Unit <span style={{ color: 'red' }}>*</span></label>
                        <select
                          value={newItem.unit}
                          onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                          style={{ fontSize: '13px' }}
                        >
                          <option value="pcs">Pieces</option>
                          <option value="kg">Kilograms</option>
                          <option value="meters">Meters</option>
                          <option value="liters">Liters</option>
                          <option value="box">Box</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '12px' }}>Unit Cost <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="number"
                          value={newItem.unitCost}
                          onChange={(e) => setNewItem({ ...newItem, unitCost: Number(e.target.value) })}
                          placeholder="0"
                          min="0"
                          step="0.01"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAddItem}
                          style={{ fontSize: '13px', padding: '8px 15px' }}
                        >
                          <MdAdd /> Add
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Items Table */}
                {items.length > 0 ? (
                  <div className="data-table-container" style={{ marginTop: '15px' }}>
                    <table className="data-table" style={{ fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th>Item Code</th>
                          <th>Item Name</th>
                          <th>System Qty</th>
                          <th>Actual Qty</th>
                          <th>Difference</th>
                          <th>Unit</th>
                          <th>Unit Cost</th>
                          <th>Adj. Value</th>
                          {!viewMode && <th>Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={index}>
                            <td className="code">{item.itemCode}</td>
                            <td>{item.itemName}</td>
                            <td style={{ textAlign: 'center' }}>{item.systemQty.toLocaleString()}</td>
                            <td style={{ textAlign: 'center' }}>{item.actualQty.toLocaleString()}</td>
                            <td style={{
                              textAlign: 'center',
                              fontWeight: 'bold',
                              color: item.difference >= 0 ? '#28a745' : '#dc3545'
                            }}>
                              {item.difference >= 0 ? '+' : ''}{item.difference.toLocaleString()}
                            </td>
                            <td>{item.unit}</td>
                            <td className="amount">₱{item.unitCost.toLocaleString()}</td>
                            <td className="amount" style={{
                              fontWeight: 'bold',
                              color: item.adjustmentValue >= 0 ? '#28a745' : '#dc3545'
                            }}>
                              ₱{item.adjustmentValue.toLocaleString()}
                            </td>
                            {!viewMode && (
                              <td>
                                <button
                                  className="btn-icon btn-delete"
                                  onClick={() => handleRemoveItem(index)}
                                  title="Remove"
                                >
                                  <MdDelete />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                          <td colSpan={7} style={{ textAlign: 'right' }}>TOTAL ADJUSTMENT VALUE:</td>
                          <td className="amount" style={{
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: items.reduce((sum, item) => sum + item.adjustmentValue, 0) >= 0 ? '#28a745' : '#dc3545'
                          }}>
                            ₱{items.reduce((sum, item) => sum + item.adjustmentValue, 0).toLocaleString()}
                          </td>
                          {!viewMode && <td></td>}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="no-data" style={{ padding: '20px', fontSize: '13px' }}>
                    <p>No items added yet</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                {viewMode ? 'Close' : 'Cancel'}
              </button>
              {!viewMode && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={!formData.location || !formData.reason || items.length === 0}
                >
                  {editingAdjustment ? 'Update Adjustment' : 'Save Adjustment'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default StockAdjustment;
